import {
  type CompanyLookupOutput,
  type CompanyRegistrySource,
  formatCompanyAddress,
} from '@fos/shared/contracts/company-registry';
import {
  COMPANY_REGISTRY_SOURCE_LABELS,
  COMPANY_VAT_STATUS_LABELS,
  COMPANY_VAT_STATUS_TONES,
} from '@fos/shared/labels';
import { formatNip, isValidNip, nipProblem, normalizeNip } from '@fos/shared/nip';
import { toLocalDate } from '@fos/shared/time';
import { Search, TriangleAlert } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { Spinner } from '../../../ui/Spinner';
import { Status } from '../../../ui/Status';

/**
 * Dane firmy po NIP (prośba Sebastiana 6.10.2026; widok bez projektu w Figmie — wzorce Field, karta podglądu, Status):
 * pole NIP „Wpisz NIP, a uzupełnimy dane firmy”, wyszukanie automatycznie po poprawnym NIP (z opóźnieniem) albo
 * „Pobierz dane”, karta podglądu (nazwa prawna, adres, REGON, KRS, status VAT — kolor tylko przy zwolnieniu
 * i braku rejestracji), „Użyj danych”, źródło i data pod spodem. Błąd rejestru = spokojny komunikat, ręczne pola
 * działają (dodanie klienta nigdy nie jest blokowane). Ostrzeżenie o kliencie z tym samym NIP z linkiem.
 */

/** „6.10.2026” (Europe/Warsaw). */
export function registryDate(value: Date | string): string {
  const [y, m, d] = toLocalDate(new Date(value)).split('-');
  return `${Number(d)}.${Number(m)}.${y}`;
}

/** „Biała lista VAT, 6.10.2026” */
export function registrySourceLabel(source: CompanyRegistrySource, at: Date | string | null | undefined): string {
  const label = COMPANY_REGISTRY_SOURCE_LABELS[source];
  return at ? `${label}, ${registryDate(at)}` : label;
}

/** Wygląda na polski NIP (cyfry, ewentualnie „PL”) — tylko wtedy pytamy rejestr. */
function looksPolish(value: string): boolean {
  return /^(pl)?[\d\s.\-–]*$/i.test(value.trim()) && /\d/.test(value);
}

/** Klienci z tym NIP (w zakresie osoby): „Ten klient już jest: REA Nieruchomości”. */
export function NipDuplicateWarning({ nip, excludeClientId }: { nip: string | null; excludeClientId?: string }) {
  const query = trpc.clients.findByNip.useQuery(
    { nip: nip ?? '', ...(excludeClientId ? { excludeClientId } : {}) },
    { enabled: Boolean(nip), staleTime: 30_000, retry: false },
  );
  const duplicates = query.data?.duplicates ?? [];
  if (!nip || duplicates.length === 0) return null;
  return (
    <div role="status" className="flex items-start gap-2 rounded-md bg-warning-subtle px-3 py-2 text-body-s text-fg">
      <TriangleAlert size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-warning" />
      <p className="min-w-0">
        {duplicates.length === 1 ? 'Klient z tym NIP już jest: ' : 'Klienci z tym NIP już są: '}
        {duplicates.map((d, i) => (
          <span key={d.id}>
            {i > 0 && ', '}
            <Link
              to={`/klienci/${d.id}`}
              className="rounded-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {d.name}
            </Link>
            {d.archived && <span className="text-fg-muted"> (archiwum)</span>}
          </span>
        ))}
      </p>
    </div>
  );
}

/** Karta podglądu danych z rejestru. */
export function CompanyPreviewCard({
  result,
  action,
}: {
  result: CompanyLookupOutput;
  /** np. „Użyj danych” */
  action?: ReactNode;
}) {
  const c = result.company;
  if (!c) return null;
  const address = formatCompanyAddress(c.address);
  return (
    <section
      aria-label="Dane firmy z rejestru"
      className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3 text-body-s"
    >
      <p className="font-medium text-fg">{c.legalName}</p>
      {address && <p className="text-fg-secondary">{address}</p>}
      <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-fg-secondary">
        <div className="flex gap-1">
          <dt className="text-fg-muted">NIP</dt>
          <dd>
            <Mono>{formatNip(c.nip)}</Mono>
          </dd>
        </div>
        {c.regon && (
          <div className="flex gap-1">
            <dt className="text-fg-muted">REGON</dt>
            <dd>
              <Mono>{c.regon}</Mono>
            </dd>
          </div>
        )}
        {c.krs && (
          <div className="flex gap-1">
            <dt className="text-fg-muted">KRS</dt>
            <dd>
              <Mono>{c.krs}</Mono>
            </dd>
          </div>
        )}
        <div>
          <dt className="sr-only">Status VAT</dt>
          <dd>
            <Status tone={COMPANY_VAT_STATUS_TONES[c.vatStatus]}>{COMPANY_VAT_STATUS_LABELS[c.vatStatus]}</Status>
          </dd>
        </div>
      </dl>
      {c.closedOn && <Status tone="warning">Działalność zakończona {registryDate(`${c.closedOn}T12:00:00Z`)}</Status>}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <p className="text-caption text-fg-muted">{registrySourceLabel(result.source, result.fetchedAt)}</p>
        {action}
      </div>
    </section>
  );
}

export interface NipLookupFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** „Użyj danych” */
  onUse: (result: CompanyLookupOutput) => void;
  /** źródło już użytych danych (pod polem zamiast karty) */
  usedLabel?: string | null;
  error?: string | null;
  autoFocus?: boolean;
  excludeClientId?: string;
}

const AUTO_LOOKUP_DELAY_MS = 600;

/** Pole NIP z wyszukaniem w rejestrze, kartą podglądu i ostrzeżeniem o duplikacie. */
export function NipLookupField({
  value,
  onChange,
  onUse,
  usedLabel,
  error,
  autoFocus,
  excludeClientId,
}: NipLookupFieldProps) {
  const lookup = trpc.clients.lookupNip.useMutation();
  const [result, setResult] = useState<CompanyLookupOutput | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // NIP, dla którego już pytaliśmy automatycznie (bez powtórek przy każdym renderze)
  const autoAsked = useRef<string | null>(null);
  const normalized = normalizeNip(value);
  const valid = isValidNip(value);
  const polish = looksPolish(value);
  const localProblem = value.trim() && polish && normalized ? nipProblem(value) : null;

  const run = (nip: string) => {
    setFailure(null);
    setResult(null);
    lookup.mutate(
      { nip },
      {
        onSuccess: (r) => setResult(r),
        onError: (err) => setFailure(errorMessage(err, 'Rejestr firm nie odpowiada. Wpisz dane ręcznie.')),
      },
    );
  };

  // automatycznie po wpisaniu poprawnego NIP (z opóźnieniem), raz dla danego numeru
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko zmiana numeru uruchamia wyszukanie
  useEffect(() => {
    if (!valid || !normalized || autoAsked.current === normalized || usedLabel) return;
    const timer = setTimeout(() => {
      autoAsked.current = normalized;
      run(normalized);
    }, AUTO_LOOKUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [normalized, valid]);

  // inny numer = stary wynik nie pasuje
  useEffect(() => {
    if (result && result.nip !== normalized) setResult(null);
    if (!valid) setFailure(null);
  }, [normalized, valid, result]);

  const hint = usedLabel
    ? `Użyto danych: ${usedLabel}`
    : polish || !value.trim()
      ? 'Wpisz NIP, a uzupełnimy dane firmy'
      : 'Numer zagraniczny — dane firmy wpisz ręcznie';

  return (
    <div className="flex flex-col gap-3">
      <Field label="NIP" hint={hint} error={error ?? localProblem}>
        <div className="flex gap-2">
          <Input
            mono
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={20}
            autoFocus={autoFocus}
            inputMode="text"
            autoComplete="off"
            placeholder="np. 526-025-02-74"
            className="min-w-0 flex-1"
          />
          <Button
            variant="secondary"
            size="l"
            icon={Search}
            disabled={!valid || lookup.isPending}
            onClick={() => normalized && run(normalized)}
          >
            Pobierz dane
          </Button>
        </div>
      </Field>

      {lookup.isPending && (
        <p className="inline-flex items-center gap-2 text-caption text-fg-secondary" role="status">
          <Spinner size={14} /> Szukamy firmy w rejestrze…
        </p>
      )}
      {failure && !lookup.isPending && (
        <div role="status" className="flex flex-wrap items-center gap-2 rounded-md bg-surface px-3 py-2 text-body-s">
          <p className="min-w-0 flex-1 text-fg-secondary">
            {failure}{' '}
            {/ręcznie/i.test(failure)
              ? 'Dodanie klienta działa bez rejestru.'
              : 'Możesz wpisać dane ręcznie — dodanie klienta działa bez rejestru.'}
          </p>
          {valid && normalized && (
            <Button variant="ghost" size="s" onClick={() => run(normalized)}>
              Spróbuj ponownie
            </Button>
          )}
        </div>
      )}
      {result && !lookup.isPending && !result.found && (
        <p role="status" className="rounded-md bg-surface px-3 py-2 text-body-s text-fg-secondary">
          Nie znaleźliśmy firmy z tym NIP w rejestrze ({registrySourceLabel(result.source, result.fetchedAt)}). Wpisz
          dane ręcznie.
        </p>
      )}
      {result?.found && !lookup.isPending && !usedLabel && (
        <CompanyPreviewCard
          result={result}
          action={
            <Button variant="secondary" size="s" onClick={() => onUse(result)}>
              Użyj danych
            </Button>
          }
        />
      )}
      <NipDuplicateWarning nip={valid ? normalized : null} excludeClientId={excludeClientId} />
    </div>
  );
}
